import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useBilingual } from '../hooks/useBilingual';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { bilingualMode, secondaryLanguage, toggleBilingual, updateSecondaryLanguage } = useBilingual();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 프로필 정보
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    grade: ''
  });

  // 비밀번호 변경
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // 로그인 확인
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'student') {
      navigate('/login');
      return;
    }

    // 현재 사용자 정보 로드
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      grade: user.grade || ''
    });
  }, [navigate]);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (profileData.name) formData.append('name', profileData.name);
      if (profileData.email) formData.append('email', profileData.email);
      if (profileData.grade) formData.append('grade', profileData.grade);

      const response = await axios.put(
        `${API_BASE_URL}/api/auth/profile`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // 로컬 스토리지 업데이트
      localStorage.setItem('user', JSON.stringify(response.data));
      setSuccess('프로필이 성공적으로 업데이트되었습니다.');
    } catch (error) {
      setError(error.response?.data?.detail || '프로필 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // 비밀번호 확인
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('새 비밀번호는 최소 6자 이상이어야 합니다.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('current_password', passwordData.currentPassword);
      formData.append('new_password', passwordData.newPassword);

      await axios.put(
        `${API_BASE_URL}/api/auth/change-password`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess('비밀번호가 성공적으로 변경되었습니다.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setError(error.response?.data?.detail || '비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>Profile</h1>
        <p>회원정보를 확인하고 수정할 수 있습니다</p>
      </div>

      {/* 탭 메뉴 */}
      <div className="tab-container">
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            👤 기본 정보
          </button>
          <button 
            className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            🔒 비밀번호 변경
          </button>
          <button 
            className={`tab-btn ${activeTab === 'language' ? 'active' : ''}`}
            onClick={() => setActiveTab('language')}
          >
            🌐 언어 설정
          </button>
        </div>

        {/* 알림 메시지 */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            {success}
          </div>
        )}

        {/* 기본 정보 탭 */}
        {activeTab === 'profile' && (
          <div className="card">
            <h3>기본 정보 수정</h3>
            <form onSubmit={updateProfile}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">이름</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  value={profileData.name}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">이메일</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              {/* <div className="form-group">
                <label htmlFor="grade" className="form-label">학습 단계</label>
                <select
                  id="grade"
                  name="grade"
                  className="form-input"
                  value={profileData.grade}
                  onChange={handleProfileChange}
                  required
                >
                  <option value="">선택</option>
                  <option value="elementary">Elementary (초등)</option>
                  <option value="youth">Youth (중고등)</option>
                  <option value="select100">Select 100 (수능 필독서)</option>
                  <option value="general">General (일반)</option>
                </select>
              </div> */}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? '업데이트 중...' : '정보 업데이트'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  뒤로가기
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 비밀번호 변경 탭 */}
        {activeTab === 'password' && (
          <div className="card">
            <h3>비밀번호 변경</h3>
            <form onSubmit={changePassword}>
              <div className="form-group">
                <label htmlFor="currentPassword" className="form-label">현재 비밀번호</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  className="form-input"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword" className="form-label">새 비밀번호</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  className="form-input"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
                <small className="form-help">최소 6자 이상 입력해주세요</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">새 비밀번호 확인</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className="form-input"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? '변경 중...' : '비밀번호 변경'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                >
                  초기화
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 언어 설정 탭 */}
        {activeTab === 'language' && (
          <div className="card">
            <h3>🌐 언어 설정</h3>
            
            {/* 바이랭귀지 모드 */}
            <div className="form-group" style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <label className="form-label" style={{ marginBottom: '5px', display: 'block' }}>
                    바이랭귀지 모드 (Bilingual Mode)
                  </label>
                  <small className="form-help" style={{ color: '#666' }}>
                    활성화 시 "한국어 (English)" 형태로 2개 언어를 동시에 표시합니다
                  </small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={bilingualMode}
                    onChange={toggleBilingual}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* 부 언어 선택 (바이랭귀지 모드 ON일 때만) */}
            {bilingualMode && (
              <div className="form-group">
                <label className="form-label">부 언어 (Secondary Language)</label>
                <small className="form-help" style={{ display: 'block', marginBottom: '10px' }}>
                  괄호 안에 표시될 언어를 선택하세요
                </small>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  <button
                    type="button"
                    className={`language-btn ${secondaryLanguage === 'ko' ? 'active' : ''}`}
                    onClick={() => updateSecondaryLanguage('ko')}
                    style={{
                      padding: '15px',
                      border: secondaryLanguage === 'ko' ? '2px solid var(--primary-color)' : '2px solid #ddd',
                      borderRadius: '8px',
                      background: secondaryLanguage === 'ko' ? 'var(--primary-color)' : 'white',
                      color: secondaryLanguage === 'ko' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🇰🇷 한국어 (Korean)
                  </button>
                  <button
                    type="button"
                    className={`language-btn ${secondaryLanguage === 'en' ? 'active' : ''}`}
                    onClick={() => updateSecondaryLanguage('en')}
                    style={{
                      padding: '15px',
                      border: secondaryLanguage === 'en' ? '2px solid var(--primary-color)' : '2px solid #ddd',
                      borderRadius: '8px',
                      background: secondaryLanguage === 'en' ? 'var(--primary-color)' : 'white',
                      color: secondaryLanguage === 'en' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🇺🇸 English (영어)
                  </button>
                  <button
                    type="button"
                    className={`language-btn ${secondaryLanguage === 'zh' ? 'active' : ''}`}
                    onClick={() => updateSecondaryLanguage('zh')}
                    style={{
                      padding: '15px',
                      border: secondaryLanguage === 'zh' ? '2px solid var(--primary-color)' : '2px solid #ddd',
                      borderRadius: '8px',
                      background: secondaryLanguage === 'zh' ? 'var(--primary-color)' : 'white',
                      color: secondaryLanguage === 'zh' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🇨🇳 中文 (Chinese)
                  </button>
                  <button
                    type="button"
                    className={`language-btn ${secondaryLanguage === 'ja' ? 'active' : ''}`}
                    onClick={() => updateSecondaryLanguage('ja')}
                    style={{
                      padding: '15px',
                      border: secondaryLanguage === 'ja' ? '2px solid var(--primary-color)' : '2px solid #ddd',
                      borderRadius: '8px',
                      background: secondaryLanguage === 'ja' ? 'var(--primary-color)' : 'white',
                      color: secondaryLanguage === 'ja' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🇯🇵 日本語 (Japanese)
                  </button>
                </div>
              </div>
            )}

            {/* 미리보기 */}
            <div className="form-group" style={{ marginTop: '30px', padding: '20px', background: '#f5f5f5', borderRadius: '8px' }}>
              <label className="form-label">미리보기 (Preview)</label>
              <div style={{ fontSize: '18px', marginTop: '10px' }}>
                {bilingualMode ? (
                  <>
                    <p>📊 내 대시보드 ({secondaryLanguage === 'ko' ? '내 대시보드' : secondaryLanguage === 'en' ? 'My Dashboard' : secondaryLanguage === 'zh' ? '我的仪表板' : 'マイダッシュボード'})</p>
                    <p>⚡ 총 XP ({secondaryLanguage === 'ko' ? '총 XP' : secondaryLanguage === 'en' ? 'Total XP' : secondaryLanguage === 'zh' ? '总经验值' : '合計XP'})</p>
                  </>
                ) : (
                  <>
                    <p>📊 내 대시보드</p>
                    <p>⚡ 총 XP</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 빠른 메뉴 */}
        <div className="card">
          <h3>빠른 메뉴</h3>
          <div className="quick-menu-grid">
            <button 
              className="quick-menu-item"
              onClick={() => navigate('/game-progress')}
            >
              <span className="quick-menu-icon">🎮</span>
              <span className="quick-menu-label">게임 진행 현황</span>
            </button>
            <button 
              className="quick-menu-item"
              onClick={() => navigate('/test')}
            >
              <span className="quick-menu-icon">🎯</span>
              <span className="quick-menu-label">게임 도전하기</span>
            </button>
            <button 
              className="quick-menu-item"
              onClick={() => navigate('/books')}
            >
              <span className="quick-menu-icon">📚</span>
              <span className="quick-menu-label">책 둘러보기</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 